import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api'; // Ajuste o caminho se necessário
import { toast } from 'react-toastify';
import { 
    FaPlus, FaEdit, FaTrash, FaBook, FaBrain, FaClipboardList, 
    FaProjectDiagram, FaSpinner, FaSitemap, FaTimes 
} from 'react-icons/fa';
import './TreinamentoDesenvolvimento.css';

// =================================================================
// === COMPONENTES INTERNOS (Modal, Loading, etc) ==================
// =================================================================

// Componente de Modal Genérico
const Modal = ({ show, onClose, title, children, footer, size = 'default' }) => {
    if (!show) return null;
    
    const modalStyle = size === 'large' ? { maxWidth: '900px', width: '90%' } : { maxWidth: '700px', width: '90%' };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">
                    {children}
                </div>
                {footer && (
                    <div className="modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

// Componente de Loading
const LoadingSpinner = () => (
    <div className="loading-spinner">
        <FaSpinner className="fa-spin" size={30} />
        <span style={{ marginLeft: '10px' }}>Carregando...</span>
    </div>
);

// =================================================================
// === ABA 1: CATÁLOGO DE CURSOS ===================================
// =================================================================
const CatalogoCursos = ({ user, cursos, loading, fetchCursos }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [cursoAtual, setCursoAtual] = useState(null);

    const initialState = {
        titulo: '',
        descricao: '',
        provedor: '',
        tipo: 'video',
        duracao_horas: 0,
        custo: 0,
        link_material: '',
        status: 'ativo'
    };

    // Funções do Modal
    const handleOpenModal = (curso = null) => {
        if (curso) {
            setIsEditMode(true);
            setCursoAtual(curso);
        } else {
            setIsEditMode(false);
            setCursoAtual(initialState);
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setCursoAtual(null);
        setIsEditMode(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCursoAtual(prev => ({ ...prev, [name]: value }));
    };

    // Funções de API
    const handleSubmit = async () => {
        try {
            if (isEditMode) {
                // Atualiza (PUT)
                await api.put(`/api/rh/cursos/${cursoAtual.id}`, cursoAtual);
                toast.success("Curso atualizado com sucesso!");
            } else {
                // Cria (POST)
                await api.post('/api/rh/cursos', cursoAtual);
                toast.success("Curso criado com sucesso!");
            }
            fetchCursos(); // Recarrega a lista
            handleCloseModal();
        } catch (error) {
            toast.error(`Falha ao ${isEditMode ? 'atualizar' : 'criar'} o curso.`);
            console.error(error);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="td-section">
            <div className="td-section-header">
                <h2>Catálogo de Cursos</h2>
                <button className="td-button-primary" onClick={() => handleOpenModal()}>
                    <FaPlus /> Novo Curso
                </button>
            </div>
            
            <table className="td-table">
                <thead>
                    <tr>
                        <th>Título</th>
                        <th>Provedor</th>
                        <th>Tipo</th>
                        <th>Duração (h)</th>
                        <th>Status</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {cursos.map(curso => (
                        <tr key={curso.id}>
                            <td>{curso.titulo}</td>
                            <td>{curso.provedor}</td>
                            <td>{curso.tipo}</td>
                            <td>{curso.duracao_horas}</td>
                            <td>{curso.status}</td>
                            <td className="actions-column">
                                <button className="edit" onClick={() => handleOpenModal(curso)} title="Editar">
                                    <FaEdit />
                                </button>
                                {/* TODO: Adicionar botão de "Inscrever" que abre modal de seleção de usuário */}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Modal de Criar/Editar Curso */}
            <Modal
                show={modalOpen}
                onClose={handleCloseModal}
                title={isEditMode ? "Editar Curso" : "Novo Curso"}
                footer={
                    <>
                        <button className="td-button-secondary" onClick={handleCloseModal}>Cancelar</button>
                        <button className="td-button-primary" onClick={handleSubmit}>Salvar</button>
                    </>
                }
            >
                <form className="td-form">
                    <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Título do Curso</label>
                        <input name="titulo" value={cursoAtual?.titulo || ''} onChange={handleChange} />
                    </div>
                    <div className="td-form-group">
                        <label>Provedor (Ex: Udemy, Interno)</label>
                        <input name="provedor" value={cursoAtual?.provedor || ''} onChange={handleChange} />
                    </div>
                    <div className="td-form-group">
                        <label>Tipo</label>
                        <select name="tipo" value={cursoAtual?.tipo || 'video'} onChange={handleChange}>
                            <option value="video">Vídeo</option>
                            <option value="presencial">Presencial</option>
                            <option value="documento">Documento</option>
                            <option value="externo">Externo</option>
                        </select>
                    </div>
                    <div className="td-form-group">
                        <label>Duração (Horas)</label>
                        <input type="number" name="duracao_horas" value={cursoAtual?.duracao_horas || 0} onChange={handleChange} />
                    </div>
                    <div className="td-form-group">
                        <label>Custo (R$)</label>
                        <input type="number" name="custo" value={cursoAtual?.custo || 0} onChange={handleChange} />
                    </div>
                    <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Link do Material/Curso</label>
                        <input name="link_material" value={cursoAtual?.link_material || ''} onChange={handleChange} />
                    </div>
                     <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Status</label>
                        <select name="status" value={cursoAtual?.status || 'ativo'} onChange={handleChange}>
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>
                    <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Descrição</label>
                        <textarea name="descricao" value={cursoAtual?.descricao || ''} onChange={handleChange}></textarea>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

// =================================================================
// === ABA 2: GESTÃO DE COMPETÊNCIAS ===============================
// =================================================================
const GestaoCompetencias = ({ user, competencias, loading, fetchCompetencias }) => {
    const [novaCompetencia, setNovaCompetencia] = useState({
        nome_competencia: '',
        descricao: '',
        tipo: 'tecnica'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNovaCompetencia(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/rh/competencias', novaCompetencia);
            toast.success("Competência criada com sucesso!");
            setNovaCompetencia({ nome_competencia: '', descricao: '', tipo: 'tecnica' });
            fetchCompetencias();
        } catch (error) {
            toast.error("Falha ao criar competência.");
            console.error(error);
        }
    };
    
    const { tecnicas, comportamentais } = useMemo(() => {
        return {
            tecnicas: competencias.filter(c => c.tipo === 'tecnica'),
            comportamentais: competencias.filter(c => c.tipo === 'comportamental')
        };
    }, [competencias]);

    if (loading) return <LoadingSpinner />;

    return (
        <>
            <div className="td-section">
                <div className="td-section-header">
                    <h2>Nova Competência</h2>
                </div>
                <form className="td-form" onSubmit={handleSubmit}>
                    <div className="td-form-group" style={{ gridColumn: 'span 2' }}>
                        <label>Nome da Competência</label>
                        <input name="nome_competencia" value={novaCompetencia.nome_competencia} onChange={handleChange} required />
                    </div>
                    <div className="td-form-group">
                        <label>Tipo</label>
                        <select name="tipo" value={novaCompetencia.tipo} onChange={handleChange}>
                            <option value="tecnica">Técnica</option>
                            <option value="comportamental">Comportamental</option>
                        </select>
                    </div>
                    <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Descrição</label>
                        <textarea name="descricao" value={novaCompetencia.descricao} onChange={handleChange}></textarea>
                    </div>
                    <div className="td-form-group" style={{ gridColumn: '1 / -1', alignItems: 'flex-end' }}>
                        <button type="submit" className="td-button-primary">Salvar Competência</button>
                    </div>
                </form>
            </div>
            
            <div className="td-section">
                 <div className="td-section-header">
                    <h2>Competências Técnicas</h2>
                </div>
                <table className="td-table">
                   <thead><tr><th>Nome</th><th>Descrição</th></tr></thead>
                   <tbody>
                       {tecnicas.map(c => <tr key={c.id}><td>{c.nome_competencia}</td><td>{c.descricao}</td></tr>)}
                   </tbody>
                </table>
            </div>
            
            <div className="td-section">
                 <div className="td-section-header">
                    <h2>Competências Comportamentais</h2>
                </div>
                <table className="td-table">
                   <thead><tr><th>Nome</th><th>Descrição</th></tr></thead>
                   <tbody>
                       {comportamentais.map(c => <tr key={c.id}><td>{c.nome_competencia}</td><td>{c.descricao}</td></tr>)}
                   </tbody>
                </table>
            </div>
        </>
    );
};

// =================================================================
// === ABA 3: MATRIZ DE CARGOS =====================================
// =================================================================
const MatrizCargos = ({ user, colaboradores, competencias, loading }) => {
    
    const [selectedCargo, setSelectedCargo] = useState('');
    const [mapaCargo, setMapaCargo] = useState([]);
    const [loadingMapa, setLoadingMapa] = useState(false);

    // Form states
    const [competenciaId, setCompetenciaId] = useState('');
    const [nivelEsperado, setNivelEsperado] = useState('basico');

    // Extrai cargos únicos dos colaboradores (via prop)
    const cargos = useMemo(() => {
        const cargosUnicos = [...new Set(colaboradores.map(c => c.cargo).filter(Boolean))];
        return cargosUnicos.sort();
    }, [colaboradores]);

    // Carrega o mapa do cargo selecionado
    useEffect(() => {
        if (!selectedCargo) {
            setMapaCargo([]);
            return;
        }
        
        const fetchMapa = async () => {
            setLoadingMapa(true);
            try {
                const { data } = await api.get(`/api/rh/competencias/mapa-cargo?cargo=${encodeURIComponent(selectedCargo)}`);
                setMapaCargo(data);
            } catch (error) {
                toast.error("Falha ao buscar mapa do cargo.");
                console.error(error);
            } finally {
                setLoadingMapa(false);
            }
        };
        fetchMapa();
    }, [selectedCargo]);
    
    const handleAddCompetencia = async (e) => {
        e.preventDefault();
        if (!competenciaId) {
            toast.warn("Selecione uma competência para adicionar.");
            return;
        }
        
        try {
            const body = {
                nome_cargo: selectedCargo,
                competencia_id: competenciaId,
                nivel_esperado: nivelEsperado
            };
            await api.post('/api/rh/competencias/mapa-cargo', body);
            toast.success("Competência adicionada ao cargo!");
            
            // Recarrega o mapa
            const { data } = await api.get(`/api/rh/competencias/mapa-cargo?cargo=${encodeURIComponent(selectedCargo)}`);
            setMapaCargo(data);
            
            // Reseta form
            setCompetenciaId('');
            setNivelEsperado('basico');
            
        } catch (error) {
            toast.error("Falha ao adicionar competência.");
            console.error(error);
        }
    };
    
    // Filtra competências que ainda não estão no mapa deste cargo
    const competenciasDisponiveis = useMemo(() => {
        const idsNoMapa = new Set(mapaCargo.map(m => m.competencia_id));
        return competencias.filter(c => !idsNoMapa.has(c.id));
    }, [competencias, mapaCargo]);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="td-section">
            <div className="td-form-group">
                <label>Selecione um Cargo para Mapear</label>
                <select value={selectedCargo} onChange={e => setSelectedCargo(e.target.value)}>
                    <option value="">-- Selecione --</option>
                    {cargos.map(cargo => (
                        <option key={cargo} value={cargo}>{cargo}</option>
                    ))}
                </select>
            </div>

            {selectedCargo && (
                <>
                    {loadingMapa ? <LoadingSpinner /> : (
                        <div className="td-section">
                            <div className="td-section-header">
                                <h2>Competências Esperadas para: {selectedCargo}</h2>
                            </div>
                            <table className="td-table">
                                <thead>
                                    <tr>
                                        <th>Competência</th>
                                        <th>Tipo</th>
                                        <th>Nível Esperado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mapaCargo.map(mapa => (
                                        <tr key={mapa.id}>
                                            <td>{mapa.nome_competencia}</td>
                                            <td>{mapa.tipo}</td>
                                            <td>{mapa.nivel_esperado}</td>
                                        </tr>
                                    ))}
                                    {mapaCargo.length === 0 && (
                                        <tr><td colSpan="3">Nenhuma competência mapeada para este cargo.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    <div className="td-section">
                        <div className="td-section-header">
                            <h2>Adicionar Competência ao Cargo</h2>
                        </div>
                        <form className="td-form" onSubmit={handleAddCompetencia}>
                            <div className="td-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Competência</label>
                                <select value={competenciaId} onChange={e => setCompetenciaId(e.target.value)}>
                                    <option value="">-- Selecione uma competência --</option>
                                    {competenciasDisponiveis.map(c => (
                                        <option key={c.id} value={c.id}>{c.nome_competencia} ({c.tipo})</option>
                                    ))}
                                </select>
                            </div>
                             <div className="td-form-group">
                                <label>Nível Esperado</label>
                                <select value={nivelEsperado} onChange={e => setNivelEsperado(e.target.value)}>
                                    <option value="basico">Básico</option>
                                    <option value="intermediario">Intermediário</option>
                                    <option value="avancado">Avançado</option>
                                </select>
                            </div>
                             <div className="td-form-group" style={{ gridColumn: '1 / -1', alignItems: 'flex-end' }}>
                                <button type="submit" className="td-button-primary">Adicionar ao Mapa</button>
                            </div>
                        </form>
                    </div>
                </>
            )}
        </div>
    );
};

// =================================================================
// === ABA 4: PLANOS DE DESENVOLVIMENTO (PDI) ======================
// =================================================================
const PlanosDesenvolvimento = ({ user, colaboradores, cursos, competencias, loading, fetchPDIs, pdise, loadingPDI }) => {
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    
    // State do PDI Mestre + Itens
    const initialPDIState = {
        titulo_pdi: '',
        data_inicio: new Date().toISOString().split('T')[0],
        data_fim_prevista: '',
        itens: [] // Array de itens
    };
    const [novoPDI, setNovoPDI] = useState(initialPDIState);

    // State do "mini-form" de item
    const initialItemState = {
        tipo_acao: 'competencia', // default
        competencia_id: '',
        curso_id: '',
        descricao_acao: ''
    };
    const [currentItem, setCurrentItem] = useState(initialItemState);

    // Busca PDIs quando o usuário selecionado muda
    useEffect(() => {
        if(selectedUser?.id) {
            fetchPDIs(selectedUser.id);
        }
    }, [selectedUser, fetchPDIs]);

    // --- Handlers do Modal Principal ---
    const handleOpenModal = () => {
        if (!selectedUser) {
            toast.warn("Por favor, selecione um colaborador primeiro.");
            return;
        }
        // Preenche o modal com dados padrão
        setNovoPDI({
            ...initialPDIState,
            titulo_pdi: `PDI ${selectedUser.nome} - ${new Date().getFullYear()}`,
        });
        setCurrentItem(initialItemState);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setNovoPDI(initialPDIState);
    };

    const handleMasterChange = (e) => {
        const { name, value } = e.target;
        setNovoPDI(prev => ({ ...prev, [name]: value }));
    };

    // --- Handlers do Mini-Form (Itens) ---
    const handleItemChange = (e) => {
        const { name, value } = e.target;
        
        // Limpa os campos relacionados ao trocar o tipo
        if (name === 'tipo_acao') {
             setCurrentItem({
                ...initialItemState,
                tipo_acao: value
             });
        } else {
            setCurrentItem(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAddItem = () => {
        // Validação
        if (currentItem.tipo_acao === 'competencia' && !currentItem.competencia_id) {
            toast.warn("Selecione uma competência."); return;
        }
        if (currentItem.tipo_acao === 'curso' && !currentItem.curso_id) {
            toast.warn("Selecione um curso."); return;
        }
        if (currentItem.tipo_acao === 'outro' && !currentItem.descricao_acao) {
            toast.warn("Descreva a ação."); return;
        }

        const itemComNome = {
            ...currentItem,
            temp_id: Date.now(), // ID temporário para a key do React
            nome: currentItem.tipo_acao === 'curso' 
                    ? cursos.find(c => c.id === parseInt(currentItem.curso_id))?.titulo
                    : currentItem.tipo_acao === 'competencia'
                    ? competencias.find(c => c.id === parseInt(currentItem.competencia_id))?.nome_competencia
                    : currentItem.descricao_acao
        };

        setNovoPDI(prev => ({
            ...prev,
            itens: [...prev.itens, itemComNome]
        }));
        setCurrentItem(initialItemState); // Reseta o mini-form
    };

    const handleRemoveItem = (temp_id) => {
        setNovoPDI(prev => ({
            ...prev,
            itens: prev.itens.filter(item => item.temp_id !== temp_id)
        }));
    };

    // --- Handler de SUBMIT (Salvar PDI e Itens) ---
    const handleSubmit = async () => {
        if (!selectedUser) return;
        
        const { itens, ...dadosGerais } = novoPDI;

        try {
            // 1. Criar o PDI mestre
            const body = { 
                ...dadosGerais, 
                user_id: selectedUser.id 
            };
            const { data } = await api.post('/api/rh/pdi', body);
            const pdiId = data.pdi_id; // Backend precisa retornar isso!

            if (!pdiId) {
                throw new Error("Backend não retornou o ID do PDI.");
            }

            // 2. Criar os itens em paralelo
            if (itens.length > 0) {
                const itemPromises = itens.map(item => {
                    return api.post(`/api/rh/pdi/${pdiId}/itens`, {
                        tipo_acao: item.tipo_acao,
                        descricao_acao: item.tipo_acao === 'outro' ? item.descricao_acao : null,
                        competencia_id: item.tipo_acao === 'competencia' ? item.competencia_id : null,
                        curso_id: item.tipo_acao === 'curso' ? item.curso_id : null,
                    });
                });
                await Promise.all(itemPromises);
            }

            toast.success("PDI e seus itens criados com sucesso!");
            fetchPDIs(selectedUser.id); // Atualiza a lista de PDIs
            handleCloseModal();

        } catch (error) {
            toast.error("Falha ao criar PDI. O PDI mestre pode ter sido criado, mas os itens falharam.");
            console.error(error.response?.data || error.message);
        }
    };
    
    // Colaboradores filtrados para o select (somente os com RH)
    const colaboradoresAdmitidos = useMemo(() => {
        return colaboradores.filter(c => c.rh_id);
    }, [colaboradores]);

    if(loading) return <LoadingSpinner />;

    return (
        <div className="td-section">
            <div className="td-form-group">
                <label>Selecione um Colaborador para ver o PDI</label>
                <select 
                    value={selectedUser?.id || ''} // Controla o valor do select
                    onChange={e => {
                        const user = colaboradores.find(c => c.id === parseInt(e.target.value));
                        setSelectedUser(user || null);
                    }}
                >
                    <option value="">-- Selecione --</option>
                    {colaboradoresAdmitidos.map(colab => (
                        <option key={colab.id} value={colab.id}>{colab.nome}</option>
                    ))}
                </select>
            </div>
            
            {selectedUser && (
                <div className="td-section">
                    <div className="td-section-header">
                        <h2>PDIs de {selectedUser.nome}</h2>
                         <button className="td-button-primary" onClick={handleOpenModal}>
                            <FaPlus /> Novo PDI
                        </button>
                    </div>
                    {loadingPDI ? <LoadingSpinner /> : (
                         <table className="td-table">
                            <thead>
                                <tr>
                                    <th>Plano</th>
                                    <th>Líder</th>
                                    <th>Status</th>
                                    <th>Início</th>
                                    <th>Prev. Fim</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pdise.map(pdi => (
                                    <tr key={pdi.id}>
                                        <td>{pdi.titulo_pdi}</td>
                                        <td>{pdi.nome_lider || 'N/A'}</td>
                                        <td>{pdi.status}</td>
                                        <td>{pdi.data_inicio ? new Date(pdi.data_inicio).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : 'N/A'}</td>
                                        <td>{pdi.data_fim_prevista ? new Date(pdi.data_fim_prevista).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : 'N/A'}</td>
                                    </tr>
                                ))}
                                {pdise.length === 0 && (
                                    <tr><td colSpan="5">Nenhum PDI cadastrado para este colaborador.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            
            {/* Modal de Novo PDI (AGORA CAPRICHADO) */}
            <Modal
                show={modalOpen}
                onClose={handleCloseModal}
                title={`Novo PDI para ${selectedUser?.nome}`}
                size="large" // Modal maior
                footer={
                    <>
                        <button className="td-button-secondary" onClick={handleCloseModal}>Cancelar</button>
                        <button className="td-button-primary" onClick={handleSubmit}>Salvar PDI Completo</button>
                    </>
                }
            >
                {/* 1. Dados Gerais */}
                <form className="td-form">
                    <div className="td-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Título do PDI</label>
                        <input name="titulo_pdi" value={novoPDI.titulo_pdi} onChange={handleMasterChange} />
                    </div>
                    <div className="td-form-group">
                        <label>Data de Início</label>
                        <input type="date" name="data_inicio" value={novoPDI.data_inicio} onChange={handleMasterChange} />
                    </div>
                    <div className="td-form-group">
                        <label>Data Prev. de Conclusão</label>
                        <input type="date" name="data_fim_prevista" value={novoPDI.data_fim_prevista} onChange={handleMasterChange} />
                    </div>
                </form>

                <hr style={{ margin: '2rem 0' }} />

                {/* 2. Itens do Plano */}
                <h3>Itens de Ação do PDI</h3>
                <div className="td-section" style={{ background: '#f9f9f9' }}>
                    <h4>Adicionar Novo Item</h4>
                    <form className="td-form">
                        <div className="td-form-group">
                            <label>Tipo de Ação</label>
                            <select name="tipo_acao" value={currentItem.tipo_acao} onChange={handleItemChange}>
                                <option value="competencia">Desenvolver Competência</option>
                                <option value="curso">Realizar Curso</option>
                                <option value="outro">Outra Ação</option>
                            </select>
                        </div>
                        
                        {/* Dropdowns Condicionais */}
                        {currentItem.tipo_acao === 'competencia' && (
                            <div className="td-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Competência</label>
                                <select name="competencia_id" value={currentItem.competencia_id} onChange={handleItemChange}>
                                    <option value="">-- Selecione a competência --</option>
                                    {competencias.map(c => <option key={c.id} value={c.id}>{c.nome_competencia} ({c.tipo})</option>)}
                                </select>
                            </div>
                        )}
                        {currentItem.tipo_acao === 'curso' && (
                             <div className="td-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Curso</label>
                                <select name="curso_id" value={currentItem.curso_id} onChange={handleItemChange}>
                                    <option value="">-- Selecione o curso --</option>
                                    {cursos.map(c => <option key={c.id} value={c.id}>{c.titulo} ({c.provedor})</option>)}
                                </select>
                            </div>
                        )}
                        {currentItem.tipo_acao === 'outro' && (
                             <div className="td-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Descrição da Ação</label>
                                <input name="descricao_acao" value={currentItem.descricao_acao} onChange={handleItemChange} />
                            </div>
                        )}
                        
                        <div className="td-form-group" style={{ justifyContent: 'flex-end' }}>
                             <button type="button" className="td-button-primary" onClick={handleAddItem}>
                                Adicionar Item
                            </button>
                        </div>
                    </form>
                </div>
                
                {/* 3. Itens Adicionados */}
                <h4>Itens a Salvar</h4>
                <table className="td-table">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Detalhe</th>
                            <th>Ação</th>
                        </tr>
                    </thead>
                    <tbody>
                        {novoPDI.itens.map(item => (
                            <tr key={item.temp_id}>
                                <td>{item.tipo_acao}</td>
                                <td>{item.nome}</td>
                                <td>
                                    <button className="actions-column delete" onClick={() => handleRemoveItem(item.temp_id)}>
                                        <FaTimes />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {novoPDI.itens.length === 0 && (
                            <tr><td colSpan="3">Nenhum item adicionado a este PDI.</td></tr>
                        )}
                    </tbody>
                </table>
            </Modal>
        </div>
    );
};


// =================================================================
// === COMPONENTE PRINCIPAL (HUB) ==================================
// =================================================================
const TreinamentoDesenvolvimento = ({ user }) => {
    const [activeTab, setActiveTab] = useState('catalogo');
    
    // --- ESTADO CENTRALIZADO ---
    const [loading, setLoading] = useState(true);
    const [cursos, setCursos] = useState([]);
    const [competencias, setCompetencias] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [pdise, setPdise] = useState([]);
    const [loadingPDI, setLoadingPDI] = useState(false);

    // --- FUNÇÕES DE FETCH CENTRALIZADAS ---
    const fetchCursos = useCallback(async () => {
        try {
            const { data } = await api.get('/api/rh/cursos');
            setCursos(data);
        } catch (error) {
            toast.error("Falha ao buscar catálogo de cursos.");
            console.error(error);
        }
    }, []);

    const fetchCompetencias = useCallback(async () => {
        try {
            const { data } = await api.get('/api/rh/competencias');
            setCompetencias(data);
        } catch (error) {
            toast.error("Falha ao buscar competências.");
            console.error(error);
        }
    }, []);

    const fetchColaboradores = useCallback(async () => {
        try {
            const { data } = await api.get('/api/rh/colaboradores');
            setColaboradores(data);
        } catch (error) {
            toast.error("Falha ao carregar colaboradores.");
            console.error(error);
        }
    }, []);
    
    // (O fetch de PDI precisa de um ID, então ele é passado como prop)
    const fetchPDIs = useCallback(async (userId) => {
        if (!userId) {
            setPdise([]);
            return;
        }
        setLoadingPDI(true);
        try {
            const { data } = await api.get(`/api/rh/pdi/colaborador/${userId}`);
            setPdise(data);
        } catch (error) {
            toast.error("Falha ao buscar PDIs do colaborador.");
            setPdise([]);
        } finally {
            setLoadingPDI(false);
        }
    }, []);


    // --- FETCH INICIAL (CARREGA TUDO) ---
    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            await Promise.all([
                fetchCursos(),
                fetchCompetencias(),
                fetchColaboradores()
            ]);
            setLoading(false);
        };
        fetchAllData();
    }, [fetchCursos, fetchCompetencias, fetchColaboradores]);


    const renderTabContent = () => {
        switch (activeTab) {
            case 'catalogo':
                return <CatalogoCursos 
                            user={user} 
                            cursos={cursos} 
                            loading={loading} 
                            fetchCursos={fetchCursos} 
                        />;
            case 'competencias':
                return <GestaoCompetencias 
                            user={user} 
                            competencias={competencias} 
                            loading={loading} 
                            fetchCompetencias={fetchCompetencias} 
                        />;
            case 'matriz':
                return <MatrizCargos 
                            user={user} 
                            colaboradores={colaboradores} 
                            competencias={competencias} 
                            loading={loading} 
                        />;
            case 'pdi':
                return <PlanosDesenvolvimento 
                            user={user} 
                            colaboradores={colaboradores} 
                            cursos={cursos} 
                            competencias={competencias}
                            loading={loading}
                            pdise={pdise}
                            loadingPDI={loadingPDI}
                            fetchPDIs={fetchPDIs}
                        />;
            default:
                return null;
        }
    };

    return (
        <div className="td-container">
            <div className="td-header">
                <h1>Treinamento & Desenvolvimento</h1>
            </div>

            {/* Navegação por Abas */}
            <div className="td-tabs">
                <button 
                    className={`td-tab-button ${activeTab === 'catalogo' ? 'active' : ''}`}
                    onClick={() => setActiveTab('catalogo')}
                >
                    <FaBook /> Catálogo de Cursos
                </button>
                <button 
                    className={`td-tab-button ${activeTab === 'competencias' ? 'active' : ''}`}
                    onClick={() => setActiveTab('competencias')}
                >
                    <FaBrain /> Gestão de Competências
                </button>
                <button 
                    className={`td-tab-button ${activeTab === 'matriz' ? 'active' : ''}`}
                    onClick={() => setActiveTab('matriz')}
                >
                    <FaSitemap /> Matriz de Cargos
                </button>
                <button 
                    className={`td-tab-button ${activeTab === 'pdi' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pdi')}
                >
                    <FaProjectDiagram /> Planos de Desenvolvimento (PDI)
                </button>
            </div>

            {/* Conteúdo da Aba */}
            <div className="td-tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default TreinamentoDesenvolvimento;